import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AppInput,
  AppPasswordInput,
  AppSelect,
  OutlineButton,
  PrimaryButton,
  StatusBadge,
} from '../../../components/ui';
import { sitesForBu, hasSitesForBu } from '../../../data/master/businessUnits';
import {
  mergeRolePermissions,
  permissionSummaryLines,
  STAFF_SCOPE_TYPES,
} from '../../../data/staff/permissions';
import { lookupBusinessUnits, lookupSites } from '../../../repositories/lookup.repository';
import { listRoles } from '../../../repositories/role.repository';
import {
  assignStaffRoles,
  createStaff,
  updateStaff,
} from '../../../repositories/staff.repository';
import { validateStaffPassword } from '../../../utils/staffPassword';

const SUPER_ADMIN_ROLE_ID = 'ROLE-SUPER';

const emptyForm = {
  fullName: '',
  email: '',
  mobile: '',
  roleIds: [],
  scopeType: 'bu',
  buId: '',
  siteId: '',
  active: true,
  password: '',
  confirmPassword: '',
};

function staffToForm(s) {
  if (!s) return emptyForm;
  return {
    fullName: s.fullName || '',
    email: s.email || '',
    mobile: s.mobile || '',
    roleIds: [...(s.roleIds || [])],
    scopeType: s.scopeType || 'bu',
    buId: s.buId || '',
    siteId: s.siteId || '',
    active: s.active !== false,
  };
}

export function StaffFormModal({
  isOpen,
  onClose,
  staff = null,
  roles: rolesProp = null,
  businessUnits: buProp = null,
  sites: sitesProp = null,
  onSaved,
}) {
  const toast = useToast();
  const editingNumericId = staff?.id ?? null;
  const editingCode = staff?.staffId || null;
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState(rolesProp || []);
  const [businessUnits, setBusinessUnits] = useState(buProp || []);
  const [sites, setSites] = useState(sitesProp || []);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      ...staffToForm(staff),
      password: '',
      confirmPassword: '',
    });
    setErrors({});
    setSaving(false);

    if (rolesProp?.length) setRoles(rolesProp);
    if (buProp?.length) setBusinessUnits(buProp);
    if (sitesProp?.length) setSites(sitesProp);

    let cancelled = false;
    (async () => {
      try {
        const needsRoles = !rolesProp?.length;
        const needsBus = !buProp?.length;
        const needsSites = !sitesProp?.length;
        if (!needsRoles && !needsBus && !needsSites) return;

        const [rolesResult, buResult, sitesResult] = await Promise.all([
          needsRoles ? listRoles({ page: 1, pageSize: 200 }) : Promise.resolve(null),
          needsBus ? lookupBusinessUnits() : Promise.resolve(null),
          needsSites ? lookupSites() : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (rolesResult) setRoles(rolesResult.items || []);
        if (buResult) setBusinessUnits(buResult || []);
        if (sitesResult) setSites(sitesResult || []);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load form options',
            status: 'error',
            position: 'top-right',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, staff, rolesProp, buProp, sitesProp, toast]);

  const buOptions = useMemo(
    () =>
      businessUnits
        .filter((b) => b.active !== false)
        .map((b) => ({
          value: b.buId,
          label: b.shortName,
          description: b.fullNameGst,
        })),
    [businessUnits],
  );

  const siteOptions = useMemo(
    () =>
      sitesForBu(sites, form.buId).map((s) => ({
        value: s.siteId,
        label: s.siteName,
        description: s.projectOperation,
      })),
    [sites, form.buId],
  );

  const hasAnySites = useMemo(
    () => (sites || []).some((s) => s.active !== false),
    [sites],
  );

  const buHasSites = hasSitesForBu(sites, form.buId);

  const scopeOptions = useMemo(() => {
    if (hasAnySites) return STAFF_SCOPE_TYPES;
    return STAFF_SCOPE_TYPES.filter((t) => t.value !== 'bu_site');
  }, [hasAnySites]);

  const showSiteField =
    form.scopeType === 'bu_site' && Boolean(form.buId) && buHasSites;

  const selectedRoles = useMemo(
    () => roles.filter((r) => form.roleIds.includes(r.roleId)),
    [roles, form.roleIds],
  );

  const mergedRights = useMemo(
    () => mergeRolePermissions(selectedRoles),
    [selectedRoles],
  );

  const rightsPreview = useMemo(
    () => permissionSummaryLines(mergedRights),
    [mergedRights],
  );

  const isSuperAdmin = form.roleIds.includes(SUPER_ADMIN_ROLE_ID);

  const patch = (partial) => {
    setForm((f) => ({ ...f, ...partial }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(partial).forEach((k) => delete next[k]);
      return next;
    });
  };

  // Drop invalid site scope when no sites exist (or BU has none)
  useEffect(() => {
    if (!hasAnySites && form.scopeType === 'bu_site') {
      setForm((f) => ({ ...f, scopeType: 'bu', siteId: '' }));
      return;
    }
    if (form.scopeType === 'bu_site' && form.buId && !buHasSites && form.siteId) {
      setForm((f) => ({ ...f, siteId: '' }));
    }
  }, [hasAnySites, form.scopeType, form.buId, buHasSites, form.siteId]);

  const toggleRole = (roleId) => {
    setForm((f) => {
      if (roleId === SUPER_ADMIN_ROLE_ID) {
        const has = f.roleIds.includes(SUPER_ADMIN_ROLE_ID);
        if (has) {
          return { ...f, roleIds: [] };
        }
        return {
          ...f,
          roleIds: [SUPER_ADMIN_ROLE_ID],
          scopeType: 'all',
          buId: '',
          siteId: '',
        };
      }
      if (f.roleIds.includes(SUPER_ADMIN_ROLE_ID)) return f;
      const has = f.roleIds.includes(roleId);
      return {
        ...f,
        roleIds: has ? f.roleIds.filter((id) => id !== roleId) : [...f.roleIds, roleId],
      };
    });
  };

  const resolveRoleDbIds = () =>
    form.roleIds
      .map((code) => roles.find((r) => r.roleId === code)?.id)
      .filter((id) => id != null);

  const resolveScopeDbIds = (scopeType, buCode, siteCode) => {
    const bu = businessUnits.find((b) => b.buId === buCode);
    const site = sites.find((s) => s.siteId === siteCode);
    const clearBu = scopeType === 'all';
    return {
      buDbId: clearBu ? null : bu?.id ?? null,
      siteDbId: scopeType === 'bu_site' ? site?.id ?? null : null,
    };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.fullName.trim()) next.fullName = 'Required';
    if (!form.email.trim()) next.email = 'Required';
    if (!form.mobile.trim()) next.mobile = 'Required';
    if (!form.roleIds.length) next.roleIds = 'Select at least one role';
    if (!isSuperAdmin && form.scopeType === 'bu' && !form.buId)
      next.buId = 'Select Business Unit';
    if (!isSuperAdmin && form.scopeType === 'bu_site') {
      if (!form.buId) next.buId = 'Select Business Unit';
      else if (!buHasSites) next.buId = 'This BU has no site — pick another BU or use BU-only scope';
      else if (!form.siteId) next.siteId = 'Select Site';
    }
    if (!editingNumericId) {
      Object.assign(next, validateStaffPassword(form.password, form.confirmPassword));
    }
    setErrors(next);
    if (Object.keys(next).length) {
      toast({ title: 'Fix highlighted fields', status: 'warning', position: 'top-right' });
      return;
    }

    const scopeType = isSuperAdmin ? 'all' : form.scopeType;
    const buCode = isSuperAdmin || scopeType === 'all' ? '' : form.buId;
    const siteCode = !isSuperAdmin && scopeType === 'bu_site' ? form.siteId : '';
    const { buDbId, siteDbId } = resolveScopeDbIds(scopeType, buCode, siteCode);
    const roleDbIds = resolveRoleDbIds();

    if (!roleDbIds.length) {
      toast({
        title: 'Could not resolve role IDs',
        status: 'error',
        position: 'top-right',
      });
      return;
    }

    const payload = {
      fullName: form.fullName.trim(),
      email: form.email.trim().toLowerCase(),
      mobile: form.mobile.trim(),
      scopeType,
      buId: buCode,
      siteId: siteCode,
      buDbId,
      siteDbId,
      roleDbIds,
      active: form.active,
    };

    setSaving(true);
    try {
      if (editingNumericId) {
        await updateStaff(editingNumericId, payload);
        await assignStaffRoles(editingNumericId, roleDbIds);
        toast({
          title: 'Staff updated',
          description: `${payload.fullName} · ${editingCode || editingNumericId}`,
          status: 'success',
          position: 'top-right',
        });
      } else {
        const created = await createStaff({
          ...payload,
          password: form.password,
        });
        toast({
          title: 'Staff created',
          description:
            created.welcomeEmailSent === false
              ? `${created.fullName} · welcome email was not sent — share the password manually`
              : `${created.fullName} · login emailed to ${payload.email}`,
          status: created.welcomeEmailSent === false ? 'warning' : 'success',
          position: 'top-right',
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save staff',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      isCentered
      scrollBehavior="inside"
      trapFocus={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>
          {editingCode ? `Edit staff · ${editingCode}` : 'Add staff user'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" pb={4}>
          <VStack align="stretch" spacing={5}>
            <Box>
              <Heading size="xs" mb={3} color="text.secondary">
                Identity
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <AppInput
                  label="Full name"
                  isRequired
                  value={form.fullName}
                  onChange={(e) => patch({ fullName: e.target.value })}
                  isInvalid={!!errors.fullName}
                  errorMessage={errors.fullName}
                />
                <AppInput
                  label="Mobile"
                  isRequired
                  value={form.mobile}
                  onChange={(e) =>
                    patch({ mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })
                  }
                  isInvalid={!!errors.mobile}
                  errorMessage={errors.mobile}
                />
                <AppInput
                  label="Email (login)"
                  isRequired
                  type="email"
                  value={form.email}
                  onChange={(e) => patch({ email: e.target.value })}
                  isInvalid={!!errors.email}
                  errorMessage={errors.email}
                />
                <Flex align="center" gap={3} pt={{ base: 0, md: 6 }}>
                  <Switch
                    isChecked={form.active}
                    onChange={(e) => patch({ active: e.target.checked })}
                    colorScheme="yellow"
                  />
                  <Text fontSize="14px" fontWeight="600">
                    Active
                  </Text>
                </Flex>
              </SimpleGrid>
              {!editingNumericId ? (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                  <AppPasswordInput
                    label="Password"
                    isRequired
                    value={form.password}
                    onChange={(e) => patch({ password: e.target.value })}
                    isInvalid={!!errors.password}
                    errorMessage={errors.password}
                    autoComplete="new-password"
                    helperText="Min 8 characters. We email this login ID and password to the staff member."
                  />
                  <AppPasswordInput
                    label="Confirm password"
                    isRequired
                    value={form.confirmPassword}
                    onChange={(e) => patch({ confirmPassword: e.target.value })}
                    isInvalid={!!errors.confirmPassword}
                    errorMessage={errors.confirmPassword}
                    autoComplete="new-password"
                  />
                </SimpleGrid>
              ) : null}
            </Box>

            <Box>
              <Heading size="xs" mb={1} color="text.secondary">
                Roles
              </Heading>
              <Text fontSize="13px" color="text.secondary" mb={3}>
                {isSuperAdmin
                  ? 'Super Admin is selected — all rights apply. Other roles are disabled.'
                  : 'What this person can do. You can assign more than one role — rights combine.'}
              </Text>
              {errors.roleIds ? (
                <Text fontSize="12px" color="status.error" mb={2}>
                  {errors.roleIds}
                </Text>
              ) : null}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                {roles
                  .filter((r) => r.active !== false)
                  .map((role) => {
                    const isSelected = form.roleIds.includes(role.roleId);
                    const isDisabled =
                      isSuperAdmin && role.roleId !== SUPER_ADMIN_ROLE_ID;
                    return (
                      <Box
                        key={role.roleId}
                        p={3}
                        bg={isSelected ? 'bg.amberTint' : 'bg.input'}
                        borderRadius="sm"
                        borderWidth="1px"
                        borderColor={isSelected ? 'utkarsh.amber' : 'border.subtle'}
                        opacity={isDisabled ? 0.45 : 1}
                        pointerEvents={isDisabled ? 'none' : 'auto'}
                      >
                        <Checkbox
                          isChecked={isSelected}
                          isDisabled={isDisabled}
                          onChange={() => toggleRole(role.roleId)}
                          colorScheme="yellow"
                        >
                          <Text fontWeight="600" fontSize="14px">
                            {role.name}
                          </Text>
                        </Checkbox>
                        <Text fontSize="12px" color="text.secondary" mt={1} ml={6}>
                          {role.description}
                        </Text>
                      </Box>
                    );
                  })}
              </SimpleGrid>
            </Box>

            <Box>
              <Heading size="xs" mb={1} color="text.secondary">
                Data scope
              </Heading>
              <Text fontSize="13px" color="text.secondary" mb={3}>
                {isSuperAdmin
                  ? 'Super Admin always has access to the entire organization.'
                  : 'Where they can use those rights — all organization, one BU, or one site.'}
              </Text>
              {isSuperAdmin ? (
                <Box
                  p={4}
                  bg="bg.input"
                  borderRadius="sm"
                  borderWidth="1px"
                  borderColor="border.subtle"
                >
                  <Text fontSize="12px" color="text.secondary" mb={1}>
                    Scope (fixed)
                  </Text>
                  <Text fontSize="15px" fontWeight="600">
                    All — entire organization
                  </Text>
                </Box>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <AppSelect
                    label="Scope"
                    isRequired
                    value={form.scopeType}
                    onChange={(v) =>
                      patch({
                        scopeType: v || 'bu',
                        buId: v === 'all' ? '' : form.buId,
                        siteId: v === 'bu_site' ? form.siteId : '',
                      })
                    }
                    options={scopeOptions}
                    helperText={
                      hasAnySites
                        ? undefined
                        : '“Business Unit + Site” is hidden until at least one site exists.'
                    }
                  />
                  {form.scopeType !== 'all' ? (
                    <AppSelect
                      label="Business Unit"
                      isRequired
                      isSearchable
                      value={form.buId}
                      onChange={(buId) => patch({ buId: buId || '', siteId: '' })}
                      options={buOptions}
                      isInvalid={!!errors.buId}
                      errorMessage={errors.buId}
                    />
                  ) : null}
                  {showSiteField ? (
                    <AppSelect
                      label="Site"
                      isRequired
                      isSearchable
                      value={form.siteId}
                      onChange={(siteId) => patch({ siteId: siteId || '' })}
                      options={siteOptions}
                      isInvalid={!!errors.siteId}
                      errorMessage={errors.siteId}
                    />
                  ) : null}
                </SimpleGrid>
              )}
            </Box>

            <Box
              p={4}
              bg="bg.input"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              <Flex align="center" gap={2} mb={2}>
                <Heading size="xs">Rights preview</Heading>
                <StatusBadge status="info">From selected roles</StatusBadge>
              </Flex>
              {rightsPreview.length ? (
                <VStack align="stretch" spacing={1}>
                  {rightsPreview.map((line) => (
                    <Text key={line} fontSize="13px" color="text.secondary">
                      • {line}
                    </Text>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="13px" color="text.secondary">
                  Select at least one role to see rights.
                </Text>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>
            {editingNumericId ? 'Save changes' : 'Create staff user'}
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
